import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faInfoCircle, faKey, faLockAlt, faMobileAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
  TextWithIcon,
  Tooltip,
} from '@modusign/design-system';
import {
  formBackgroundNeutralSlightlyDark,
  formBorderNeutralBase,
} from '@modusign/design-system/colors';
import { useSigner, useVerification } from '@modusign/react-signing';
import { VerificationMethod, VerificationMethodType } from '@modusign/react-signing/types';

import { useSelectionValues } from '@hooks';

const DEFAULT_VERIFICATION_METHODS: Record<VerificationMethodType, VerificationMethod> = {
  PASSWORD: {
    type: 'PASSWORD',
    hint: null,
    passValue: '',
  },
  MOBILE_IDENTIFICATION: {
    type: 'MOBILE_IDENTIFICATION',
    hint: null,
    passValue: '',
    displayName: null,
    displayPhoneNumber: null,
  },
  D_CERT: {
    type: 'D_CERT',
    hint: null,
    passValue: '',
    displayName: null,
    displayBizNumber: null,
  },
};

const VERIFICATION_METHOD_TYPES: VerificationMethodType[] = [
  'PASSWORD',
  'MOBILE_IDENTIFICATION',
  'D_CERT',
];

interface TemplateSignerVerificationModalProps {
  signerId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function TemplateSignerVerificationModal({
  signerId,
  isOpen,
  onClose,
}: TemplateSignerVerificationModalProps) {
  const { addMethod, removeMethod, verification } = useVerification(signerId);
  const { signer } = useSigner(signerId);

  const signingMethod = signer?.signingMethod ?? null;

  const { selected, toggle } = useSelectionValues<VerificationMethodType>(
    verification?.methods.map((method) => method.type) ?? []
  );

  const handleVerificationSave = () => {
    VERIFICATION_METHOD_TYPES.forEach((method) => {
      if (selected.includes(method)) {
        addMethod(DEFAULT_VERIFICATION_METHODS[method]);
        return;
      }
      removeMethod(method);
    });
  };

  const validateCorporateDigitalCertification = () => {
    if (signingMethod === 'KAKAO') {
      return {
        message: (
          <Text>
            카카오톡 서명자에게는 법인 공동인증서 인증을
            <Box as="br" />
            설정할 수 없습니다.
          </Text>
        ),
        isValid: false,
      };
    }

    if (signingMethod === 'IN_PERSON') {
      return {
        message: (
          <Text>
            대면서명자에게는 법인 공동인증서 인증을
            <Box as="br" />
            설정할 수 없습니다.
          </Text>
        ),
        isValid: false,
      };
    }
    if (selected.includes('MOBILE_IDENTIFICATION')) {
      return {
        message: (
          <Text>
            휴대폰 본인 인증 설정 시 <Box as="br" />
            법인 공동인증서 인증을 설정할 수 없습니다.
          </Text>
        ),
        isValid: false,
      };
    }
    return {
      message: null,
      isValid: true,
    };
  };

  const dCertValidationResult = validateCorporateDigitalCertification();

  return (
    <Modal isOpen={isOpen} maxWidth="sm" onClose={onClose}>
      <ModalTitle mb={4}>추가 인증 수단 설정</ModalTitle>
      <ModalContent>
        <Text mb={7} textAlign="center">
          {signer?.role ?? '서명자'}의 추가 인증 수단을 선택해 주세요.
        </Text>
        <Flex flexDirection="column" gap={4}>
          <VerificationCheckbox
            content={{
              title: '접근 암호 인증',
              description: '서명자는 암호를 입력한 후, 서명할 수 있습니다.',
              icon: faLockAlt,
            }}
            isChecked={selected.includes('PASSWORD')}
            onClick={() => toggle('PASSWORD')}
          />
          <Tooltip
            content={
              <Text>
                법인 공동 인증서 인증 설정 시 <Box as="br" /> 휴대폰 본인 인증을 설정할 수 없습니다.
              </Text>
            }
            disabled={!selected.includes('D_CERT')}
            placement="right"
          >
            <Box width="100%">
              <VerificationCheckbox
                content={{
                  title: '휴대폰 본인 인증',
                  description: '서명자는 휴대폰 본인 인증 후, 서명할 수 있습니다.',
                  icon: faMobileAlt,
                }}
                disabled={selected.includes('D_CERT')}
                isChecked={selected.includes('MOBILE_IDENTIFICATION')}
                onClick={() => toggle('MOBILE_IDENTIFICATION')}
              />
            </Box>
          </Tooltip>
          <Tooltip
            content={dCertValidationResult.message}
            disabled={dCertValidationResult.isValid}
            placement="right"
          >
            <Box width="100%">
              <VerificationCheckbox
                content={{
                  title: '법인 공동인증서 인증',
                  description: '서명자가 법인인 경우 공동인증서로 인증 후, 서명할 수 있습니다.',
                  icon: faKey,
                }}
                disabled={!dCertValidationResult.isValid}
                isChecked={selected.includes('D_CERT')}
                onClick={() => toggle('D_CERT')}
              />
            </Box>
          </Tooltip>
        </Flex>
        <TextWithIcon
          color="textNeutralSlightlyLight"
          iconContainerWidth="11px"
          leftIcon={<Box as={FontAwesomeIcon} color="textNeutralLight" icon={faInfoCircle} />}
          mt={7}
        >
          대량전송은 엑셀을 통해 추가 인증 수단을 간편하게 설정할 수 있습니다.
        </TextWithIcon>
      </ModalContent>
      <ModalFooter>
        <Button onClick={onClose}>취소</Button>
        <Button
          disabled={selected.includes('MOBILE_IDENTIFICATION') && selected.includes('D_CERT')}
          ml={3}
          variant="primary"
          onClick={() => {
            handleVerificationSave();
            onClose();
          }}
        >
          설정하기
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface VerificationCheckboxProps {
  content: {
    title: string;
    description: string;
    icon: IconProp;
  };
  isChecked: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const VerificationCheckbox = ({
  content,
  isChecked,
  onClick,
  disabled,
}: VerificationCheckboxProps) => {
  return (
    <Flex
      as="button"
      backgroundColor={formBackgroundNeutralSlightlyDark}
      border="1px solid"
      borderColor={formBorderNeutralBase}
      disabled={disabled}
      flexDirection="column"
      gap={4}
      p={6}
      sx={{
        '&:hover': {
          backgroundColor: disabled
            ? 'buttonBackgroundNeutralDark'
            : 'formBackgroundNeutralSlightlyDark',
        },
      }}
      width="100%"
      onClick={onClick}
    >
      <Flex alignItems="center" gap={2}>
        <Checkbox checked={isChecked} disabled={disabled} />
        <Box
          as={FontAwesomeIcon}
          color={disabled ? 'iconNeutralBase' : 'iconPrimaryBase'}
          icon={content.icon}
        />
        <Text
          color={disabled ? 'textNeutralUltraLight' : 'textNeutralBase'}
          fontSize="lg"
          fontWeight="medium"
        >
          {content.title}
        </Text>
      </Flex>
      <Text color={disabled ? 'textNeutralUltraLight' : 'textNeutralBase'} fontSize="sm">
        {content.description}
      </Text>
    </Flex>
  );
};
